import {
  buildCargoFromNormalized,
  canViewModule,
  getNormalizedPermissions,
  type ColaboradorCargoJson,
  type NormalizedCargoPermissions,
} from "../types/collaboratorCargo";
import {
  getModuleById,
  type PermissionActionId,
} from "../types/collaboratorPermissionRegistry";

/** Título fixo do cargo interno — não usar `nome_completo` aqui. */
export const DELIVAI_DEVELOPER_CARGO_TITLE = "Dev DelivAI";

/** Título fixo do cargo advogado interno. */
export const DELIVAI_LAWYER_CARGO_TITLE = "Advogado DelivAI";

/** Título fixo do cargo personalizado (permissoes ajustadas depois). */
export const DELIVAI_CUSTOM_CARGO_TITLE = "Equipe DelivAI";

/**
 * Cargo v2 para novo dev interno: módulo `delivai_controle` com todas as ações liberadas.
 * Gravado em `colaboradores.cargo` via webhook `colaboradores` (action `new`), não no JSON da empresa.
 */
export function buildDelivaiDeveloperCargo(): ColaboradorCargoJson {
  const mod = getModuleById("delivai_controle");
  if (!mod) {
    return buildCargoFromNormalized(DELIVAI_DEVELOPER_CARGO_TITLE, {});
  }
  const a: Partial<Record<PermissionActionId, boolean>> = {};
  for (const act of mod.actions) {
    a[act.id] = true;
  }
  const norm: NormalizedCargoPermissions = {
    delivai_controle: { v: true, a },
  };
  return buildCargoFromNormalized(DELIVAI_DEVELOPER_CARGO_TITLE, norm);
}

/** Cargo advogado: somente Início + Contratos. */
export function buildDelivaiLawyerCargo(): ColaboradorCargoJson {
  const norm: NormalizedCargoPermissions = {
    delivai_controle: {
      v: true,
      a: {
        ver: true,
        editar_contratos: true,
      },
    },
  };
  return buildCargoFromNormalized(DELIVAI_LAWYER_CARGO_TITLE, norm);
}

/** Cargo minimo: so acessa o painel interno; ajuste granular depois no editor. */
export function buildDelivaiCustomCargo(): ColaboradorCargoJson {
  const norm: NormalizedCargoPermissions = {
    delivai_controle: {
      v: true,
      a: { ver: true },
    },
  };
  return buildCargoFromNormalized(DELIVAI_CUSTOM_CARGO_TITLE, norm);
}

export type DelivaiStaffRolePreset = "developer" | "lawyer" | "custom";

export function buildDelivaiCargoForRolePreset(preset: DelivaiStaffRolePreset): ColaboradorCargoJson {
  if (preset === "lawyer") return buildDelivaiLawyerCargo();
  if (preset === "custom") return buildDelivaiCustomCargo();
  return buildDelivaiDeveloperCargo();
}

const INTERNAL_MASTER_CARGO_TITLES = new Set([
  "admin",
  "administrador",
  "gerente",
  "desenvolvedor",
  "developer",
  DELIVAI_DEVELOPER_CARGO_TITLE.toLowerCase(),
]);

/** Cargos internos que devem ter acesso total ao painel /dev. */
export function isDelivaiInternalMasterCargoTitulo(titulo: string | undefined | null): boolean {
  const t = (titulo || "").trim().toLowerCase();
  return t.length > 0 && INTERNAL_MASTER_CARGO_TITLES.has(t);
}

/**
 * Permissões efetivas para colaborador interno DelivAI.
 * Admin/gerente/dev recebem cargo developer completo; legado sem `delivai_controle` ganha o módulo.
 */
export function resolveInternalDevNormalizedPermissions(
  cargoJson: ColaboradorCargoJson
): NormalizedCargoPermissions {
  const norm = getNormalizedPermissions(cargoJson);
  if (isDelivaiInternalMasterCargoTitulo(cargoJson.titulo)) {
    return getNormalizedPermissions(buildDelivaiDeveloperCargo());
  }
  if (!canViewModule(norm, "delivai_controle")) {
    const devNorm = getNormalizedPermissions(buildDelivaiDeveloperCargo());
    return { ...norm, delivai_controle: devNorm.delivai_controle };
  }
  return norm;
}

/** Normaliza cargo gravado no Postgres para sessão interna (login / refresh). */
export function ensureDelivaiInternalDevCargo(cargoJson: ColaboradorCargoJson): ColaboradorCargoJson {
  const norm = resolveInternalDevNormalizedPermissions(cargoJson);
  return buildCargoFromNormalized(cargoJson.titulo.trim() || DELIVAI_DEVELOPER_CARGO_TITLE, norm);
}
